import {
  associateRefreshToken,
  getRefreshToken,
  getRefreshTokenForOrganization,
  getRefreshTokensForRegion,
  storeRefreshToken,
} from '~/lib/auth/refresh-token-storage';
import {request} from './client';
import {AccessDeniedError, NotFoundError} from './errors';
import type {
  RawTokenResponse,
  TokenResponse,
  TokenSuccessResponse,
} from './types';
import {isTokenSuccess, normalizeTokenResponse} from './types';

function isDefinitiveRejection(err: unknown): boolean {
  return err instanceof AccessDeniedError || err instanceof NotFoundError;
}

async function requestWithCredential(
  identifier: string,
  refreshToken: string | null,
): Promise<TokenResponse> {
  const raw = await request<RawTokenResponse>(
    identifier,
    `/sharing/tokens/${identifier}`,
    {
      method: 'POST',
      headers: refreshToken
        ? {Authorization: `Bearer ${refreshToken}`}
        : undefined,
    },
  );

  return normalizeTokenResponse(raw);
}

async function persistSuccessfulResponse(
  identifier: string,
  response: TokenSuccessResponse,
  sentRefreshToken: string | null,
): Promise<TokenResponse> {
  const {
    organization_identifier: organizationIdentifier,
    refresh_token: newRefreshToken,
    refresh_token_expires_at: newRefreshTokenExpiresAt,
  } = response.data;

  if (!newRefreshToken || !newRefreshTokenExpiresAt) {
    if (sentRefreshToken) {
      associateRefreshToken(
        identifier,
        organizationIdentifier,
        sentRefreshToken,
      );
    }
    return response;
  }

  const existingOrganizationToken = getRefreshTokenForOrganization(
    organizationIdentifier,
  );

  // Re-request with the established org credential so the JWT keeps its identity.
  if (
    existingOrganizationToken &&
    existingOrganizationToken !== sentRefreshToken
  ) {
    let retriedResponse: TokenResponse | undefined;
    try {
      retriedResponse = await requestWithCredential(
        identifier,
        existingOrganizationToken,
      );
    } catch (err) {
      if (isDefinitiveRejection(err)) {
        throw err;
      }
    }

    if (retriedResponse && isTokenSuccess(retriedResponse)) {
      return persistSuccessfulResponse(
        identifier,
        retriedResponse,
        existingOrganizationToken,
      );
    }
    associateRefreshToken(
      identifier,
      organizationIdentifier,
      existingOrganizationToken,
    );
    return response;
  }

  storeRefreshToken(
    identifier,
    organizationIdentifier,
    newRefreshToken,
    newRefreshTokenExpiresAt,
    {anonymous: true},
  );
  return response;
}

export async function requestToken(identifier: string): Promise<TokenResponse> {
  const preferredToken = getRefreshToken(identifier);
  const response = await requestWithCredential(identifier, preferredToken);
  if (isTokenSuccess(response)) {
    return persistSuccessfulResponse(identifier, response, preferredToken);
  }

  // verification_required may only mean the credential belongs to another org.
  for (const refreshToken of getRefreshTokensForRegion(identifier)) {
    if (refreshToken === preferredToken) {
      continue;
    }
    let candidate: TokenResponse;
    try {
      candidate = await requestWithCredential(identifier, refreshToken);
    } catch (err) {
      if (isDefinitiveRejection(err)) {
        throw err;
      }
      continue;
    }
    if (isTokenSuccess(candidate)) {
      return persistSuccessfulResponse(identifier, candidate, refreshToken);
    }
  }

  return response;
}
